import { cx } from '@emotion/css';
import { Button } from '@sortlist-frontend/design-system';
import { Divider } from '@sortlist-frontend/design-system/legacy';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import { Fragment, useState } from 'react';

import { ToggleButton } from '_components/common/ToggleButton';
import { usePublicAppContext } from '_core/context/public-app-context';
import { agencyProfileConfig } from '_features/AgencyProfile/config';
import { useGetAgencyAwards } from '_features/AgencyProfile/hooks/useGetAgencyAwards';

import { SectionBox } from '../SectionBox';
import { Award, type AwardNode } from './Award';

const INITIAL_DISPLAYED_AWARDS = 4;

interface Props {
  children?: never;
  className?: string;
  agencyName?: string;
}

export const AwardsPanel = (props: Props) => {
  const { className, agencyName } = props;
  const { t } = useTranslation(agencyProfileConfig.i18nNamespaces);
  const { locale, pageData } = usePublicAppContext();
  const { agencySlug } = pageData as { agencySlug: string };
  const [toggledAllAwards, setToggledAllAwards] = useState(false);
  const {
    awards: awardsData,
    hasNextPage,
    isFetchingMore,
    fetchMoreAwards,
  } = useGetAgencyAwards({ agencySlug, locale: locale as string });

  const awards = awardsData?.nodes?.filter((n): n is AwardNode => n != null) ?? [];
  const awardsCount = awardsData?.totalCount ?? 0;

  return (
    <SectionBox
      id="awards"
      title={t('agency:profile.awards.title')}
      className={cx(className, 'sm:py-24 max-sm:py-16')}
      subtitle={
        <Trans
          i18nKey={'agency:profile.awards.subtitle'}
          count={awardsCount}
          values={{ name: agencyName, count: awardsCount }}>
          {'{{name}}'} was
          <b>awarded {'{{count}}'} times</b>
        </Trans>
      }>
      {awardsCount > 0 ? (
        <div className="lg:px-14 px-6 relative">
          <div className="layout-xs-column layout-row layout-wrap layout-align-start-stretch">
            {awards
              .slice(0, toggledAllAwards ? awards.length : INITIAL_DISPLAYED_AWARDS)
              .map((award, index: number) => (
                <div key={`${award.name}-${index}`} className="flex-100 flex-sm-50 flex-gt-sm-25 layout-column">
                  <Award award={award} className="flex-1"></Award>
                </div>
              ))}
          </div>
          {hasNextPage && toggledAllAwards ? (
            <div className="layout-row layout-align-center-center py-4">
              <Button
                variant="light"
                className="underline"
                color="secondary"
                size="sm"
                isLoading={isFetchingMore}
                onPress={fetchMoreAwards}>
                {t('common:clampLines.seeMore')}
              </Button>
            </div>
          ) : null}
          {awardsCount > INITIAL_DISPLAYED_AWARDS && (!toggledAllAwards || !hasNextPage) ? (
            <div className="layout-column layout-align-cener-center">
              <ToggleButton
                handleToggled={() => setToggledAllAwards(!toggledAllAwards)}
                seeMoreBtnLabel={
                  <Trans i18nKey={'agency:profile.awards.seeMoreAwards'} count={awardsCount}>
                    See all other awards ({{ count: awardsCount - INITIAL_DISPLAYED_AWARDS }})
                  </Trans>
                }
                seeLessBtnLabel={t('agency:profile.awards.seeLessAwards')}
                toggled={toggledAllAwards}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </SectionBox>
  );
};
